/* eslint-disable no-console */
/* eslint-disable class-methods-use-this */
import { Event, Map, MapSourceDataEvent } from "@iventis/mapbox-gl";
import { Polygon } from "geojson";
import { BehaviorSubject } from "rxjs";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { MaskLayer } from "../generic-layers/mask-layer";
import { iventisAreaToAggregateLayer } from "../mapbox/engine-mapbox-helpers";
import { SupportedMapboxLayer } from "../mapbox/sublayer-types";
import { MapState } from "../../types/store-schema";
import { IVENTIS_MAP_BACKGROUND_SLOT_ID } from "../constants";

export class MapboxMaskLayer extends MaskLayer {
    private readonly map: Map;

    private mapboxLayers: SupportedMapboxLayer[] = [];

    private mouseOverRef: (ev: Event) => void;

    private mouseLeaveRef: (ev: Event) => void;

    private removeSourceOnLoadRef: (ev: MapSourceDataEvent) => void;

    private previousCursor: string | null = null;

    constructor(map: Map, polygons: Polygon[], state$: BehaviorSubject<MapState>) {
        super(polygons, state$);
        this.map = map;
        this.addLocalSource();
        this.addLayer();
        this.onMouseEvents();
    }

    protected addLayer() {
        this.mapboxLayers = iventisAreaToAggregateLayer(this.maskLayer, false, { projectDataFields: [], unitOfMeasurement: UnitOfMeasurement.Metric }, null).map(
            (layer) => layer.layer
        );

        const mapBackgroundSlotIndex = this.map.getStyle().layers.findIndex((l) => l.id === IVENTIS_MAP_BACKGROUND_SLOT_ID);
        const bottomCadLayer = this.map.getStyle().layers[mapBackgroundSlotIndex + 1];

        this.mapboxLayers.forEach((layer) => {
            try {
                this.map.addLayer(layer, bottomCadLayer?.id);
            } catch (error) {
                console.error("Error adding mask layer to map", { layerId: layer.id, error });
            }
        });
    }

    protected removeLayer() {
        this.mapboxLayers.forEach((layer) => {
            if (this.map.getLayer(layer.id)) {
                this.map.removeLayer(layer.id);
            }
        });
    }

    protected addLocalSource() {
        // Check if the source already exists before adding it
        const source = this.map.getSource(this.sourceId);
        if (source) {
            return;
        }
        try {
            this.map.addSource(this.sourceId, { type: "geojson", data: { type: "FeatureCollection", features: [this.source] } });
        } catch (error) {
            console.error("Error adding mask source to map", { sourceId: this.sourceId, error });
        }
    }

    protected removeLocalSource() {
        const source = this.map.getSource(this.sourceId);

        if (source && source.type === "geojson") {
            // If source is not loaded, wait until map is idle (source will be loaded) and remove
            if (source.loaded()) {
                this.map.removeSource(this.sourceId);
            } else {
                this.removeSourceOnLoadRef = this.removeSourceOnLoad.bind(this);
                // Check when the source has loaded, then remove it
                this.map.on("sourcedata", this.removeSourceOnLoadRef);
            }
        }
    }

    public updateLocalSource(updatedPolygon: Polygon[]) {
        super.updateLocalSource(updatedPolygon);
        const source = this.map.getSource(this.sourceId);
        if (source && source.type === "geojson") {
            source.setData({ type: "FeatureCollection", features: [this.source] });
        }
    }

    protected onMouseEvents() {
        // Add the mouse over event
        this.mouseOverRef = this.mouseOver.bind(this);
        this.map.on("mouseover", this.layerId, this.mouseOverRef);

        // Add the mouse leave event
        this.mouseLeaveRef = this.mouseLeave.bind(this);
        this.map.on("mouseleave", this.layerId, this.mouseLeaveRef);
    }

    protected updateStyle(): void {
        throw new Error("Method not implemented.");
    }

    protected hideLayer(): void {
        throw new Error("Method not implemented.");
    }

    protected showLayer(): void {
        throw new Error("Method not implemented.");
    }

    protected updateMapOrder(): void {
        throw new Error("Method not implemented.");
    }

    protected updateMapLevel(): void {
        throw new Error("Method not implemented.");
    }

    public getLayerIdsForSnapping(): string[] {
        throw new Error("Method not implemented.");
    }

    private mouseOver() {
        const changeCursor = this.state$.value.mapObjectsSelected.value.length !== 0 || this.state$.value.mode.value.includes("composition");
        if (changeCursor) {
            this.previousCursor = this.map.getCanvas().style.cursor;
            this.map.getCanvas().style.cursor = "not-allowed";
        }
    }

    private mouseLeave() {
        const currentCursor = this.map.getCanvas().style.cursor;
        // Change cursor back to previous cursor if it was not changed
        if (this.previousCursor != null && currentCursor === "not-allowed") {
            this.map.getCanvas().style.cursor = this.previousCursor;
        }
    }

    private removeSourceOnLoad(event: MapSourceDataEvent) {
        if (event.sourceId === this.sourceId) {
            this.removeLocalSource();
            this.map.off("sourcedata", this.removeSourceOnLoad);
        }
    }

    public remove() {
        this.removeLayer();
        this.removeLocalSource();
        this.map.off("mouseover", this.layerId, this.mouseOverRef);
        this.map.off("mouseleave", this.layerId, this.mouseLeaveRef);
    }
}
